import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { FormikHelpers } from "formik";
import _ from "lodash";
import { ReactElement } from "react";
import * as yup from "yup";

import { plainScriptFieldValidator } from "~frontend/components/forms/FormPlainScriptField";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import { FormikDialog } from "~frontend/components/forms/FormikDialog";
import FormikErrorBanner from "~frontend/components/forms/FormikErrorBanner";
import {
  MediaFieldValue,
  mediaValidationSchema,
} from "~frontend/components/forms/FormikMediaField";
import { useTenant } from "~frontend/providers/LoggedInUserProvider";
import { MEDIA_FILE_MAX_SIZE_BYTES } from "~frontend/util/mediaFiles";

import { ScriptEditor } from "../../campaignEditor/Script/ScriptEditor";
import ShaftWarnings from "../../campaignEditor/Script/ShaftWarnings";

export interface KeywordValues {
  keyword: string;
  script: string;
  _submitError: any;
  media?: MediaFieldValue | null;
}

export default function KeywordDialogForm({
  open,
  onClose,
  onSubmit,
  initialValues,
  dialogTitle,
  dialogCopy,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    values: KeywordValues,
    formik: FormikHelpers<KeywordValues>
  ) => void;
  initialValues: KeywordValues;
  dialogTitle: string;
  dialogCopy: string;
}): ReactElement {
  const tenant = useTenant()!;

  return (
    <FormikDialog
      open={open}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={yup.object().shape({
        keyword: yup.string().required("Required").min(1).max(100),
        script: plainScriptFieldValidator({ strictLengthLimit: true }, tenant),
        media: mediaValidationSchema(),
      })}
    >
      {({ isSubmitting, values, errors }) => {
        return (
          <>
            <DialogTitle sx={{ pb: 0 }}>{dialogTitle}</DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
              <Typography sx={{ mb: 2 }}>{dialogCopy}</Typography>
              <FormikErrorBanner />
              <FormikTextField
                fullWidth
                label="Keyword"
                name="keyword"
                inputProps={{ style: { textTransform: "uppercase" } }}
              />
              <ScriptEditor
                type={"keyword"}
                script={values.script}
                media={values.media || null}
                hideContactFields={true}
                hidePreview={true}
                defaultAlert={
                  <Alert severity="warning">
                    {`Media files can be a picture, video, GIF, or contact card.
                    Files must be jpg, png, gif, mp4, or vcard format and under
                    ${MEDIA_FILE_MAX_SIZE_BYTES / 1000} KB.`}
                  </Alert>
                }
              />
              <ShaftWarnings renderedMessage={values.script} />
            </DialogContent>
            <DialogActions>
              <Button
                disabled={isSubmitting}
                onClick={onClose}
                color="tertiary"
                variant="contained"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || Object.keys(errors).length > 0}
                color={"primary"}
                variant="contained"
              >
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </>
        );
      }}
    </FormikDialog>
  );
}
